import React, { useState, useRef, useEffect } from 'react';
import { Camera, Mail, Lock, Globe, Bell, Eye, EyeOff } from 'lucide-react';
import { useStore } from '../../store/useStore';
import 'react-phone-number-input/style.css';
import {
  getCountries,
  getCountryCallingCode,
  parsePhoneNumberFromString,
  getExampleNumber,
} from "libphonenumber-js";

const countries = getCountries();

interface CustomPhoneInputProps {
  value: string;
  onChange: (value: string) => void;
}

function getFlagEmoji(countryCode: string) {
  return countryCode
    .toUpperCase()
    .replace(/./g, (char) => String.fromCodePoint(127397 + char.charCodeAt(0)));
}

function CustomPhoneInput({ value, onChange }: CustomPhoneInputProps) {
  const [country, setCountry] = useState("UK");
  const [number, setNumber] = useState("");

  useEffect(() => {
    const phoneNumber = parsePhoneNumberFromString(value || "");
    if (phoneNumber?.country) {
      setCountry(phoneNumber.country);
      setNumber(phoneNumber.nationalNumber);
    }
  }, [value]);

  const handleCountryChange = (e: React.ChangeEvent<HTMLSelectElement>) => {
    const newCountry = e.target.value;
    setCountry(newCountry);
    updatePhone(newCountry, number);
  };

  const handleNumberChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const newNumber = e.target.value.replace(/\D/g, ""); // Strip non-numeric
    setNumber(newNumber);
    updatePhone(country, newNumber);
  };

  const updatePhone = (countryCode: string, nationalNum: string) => {
    const full = `+${getCountryCallingCode(countryCode as any)}${nationalNum}`;
    onChange(full);
  };

  const isValid = parsePhoneNumberFromString(value || "")?.isValid();

  return (
    <div className="space-y-1">
      <div className="flex items-center space-x-2">
        <select
          value={country}
          onChange={handleCountryChange}
          className="w-32 px-2 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        >
          {countries.map((c) => (
            <option key={c} value={c}>
              {getFlagEmoji(c)} {c} +{getCountryCallingCode(c)}
            </option>
          ))}
        </select>

        <input
          type="tel"
          value={number}
          onChange={handleNumberChange}
          placeholder="Phone number"
          className="flex-1 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
        />
      </div>

      <p className={`text-sm ${isValid ? "text-green-600" : "text-red-500"}`}>
        {value
          ? isValid
            ? "✅ Valid number"
            : "❌ Invalid number"
          : "Enter phone number"}
      </p>
    </div>
  );
}

interface UserProfile {
  name: string;
  email: string;
  avatar: string;
  location: string;
  timezone?: string;
  bio?: string;
  phone?: string;
  measurementSystem: 'metric' | 'imperial';
  notifications: {
    email: boolean;
    newRecipes: boolean;
    weeklyDigest: boolean;
    specialOffers: boolean;
  };
  userPreferences: {
    mealTimes: Array<{
      type: 'breakfast' | 'lunch' | 'dinner';
      time: string;
    }>;
  };
}

const Profile: React.FC = () => {
  const user = useStore((state) => state.user);
  // console.log('User:', user);
  const [profile, setProfile] = useState<UserProfile>({
    name: `${user?.firstName || ''} ${user?.lastName || ''}`.trim() || 'Guest User',
    email: user?.email || '',
    avatar: user?.avatar || 'https://t3.ftcdn.net/jpg/05/57/20/16/360_F_557201692_P86sh0v8g00VseZacjBOOKJmGLSvEpQb.jpg',
    location: user?.location || '',
    timezone: user?.timeZone || '',
    bio: user?.bio || '',
    phone: user?.phone || '',
    measurementSystem: 'imperial',
    notifications: {
      email: true,
      newRecipes: true,
      weeklyDigest: true,
      specialOffers: false
    },
    userPreferences: {
      mealTimes: [
        { type: 'breakfast', time: '07:00' },
        { type: 'lunch', time: '12:00' },
        { type: 'dinner', time: '18:00' }
      ]
    }
  });

  // console.log('Profile:', profile);
  // console.log('User:', user);
  // Sync profile when `user` changes
  useEffect(() => {
    if (user) {
      setProfile((prevProfile) => ({
        ...prevProfile,
        name: `${user.firstName || ''} ${user.lastName || ''}`.trim() || 'Guest User',
        email: user.email || '',
        avatar: user.avatar || prevProfile.avatar,
        location: user.location || prevProfile.location,
        timezone: user.timeZone || prevProfile.timezone,
        bio: user.bio || prevProfile.bio
      }));
    }
  }, []);

  const [activeTab, setActiveTab] = useState<'info' | 'preferences' | 'security'>('info');
  const [isEditing, setIsEditing] = useState<boolean>(false);
  const [formData, setFormData] = useState<UserProfile>(profile);
  const [currentPassword, setCurrentPassword] = useState('');
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [passwordError, setPasswordError] = useState('');
  const [showCurrentPassword, setShowCurrentPassword] = useState(false);
  const [showNewPassword, setShowNewPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  const location = useStore((state) => state.location) || { country: 'US', currency: 'USD', currencySymbol: '$' };
  const [country, setCountry] = useState<any>(location.country);
  const updateLocation = useStore((state) => state.updateLocation);
  const updateUserProfile = useStore((state) => state.updateProfile);
  const updateUserPassword = useStore((state) => state.updateUserPassword);
  const updatePreferences = useStore((state) => state.updatePreferences);
  const addNotification = useStore((state) => state.addNotification);
  const updateMealTime = useStore((state) => state.updateMealTime);
  const mealTime = useStore((state) => state.mealTime);
  const [mealTimes, setMealTimes] = useState(mealTime ?? {
    breakfast: '07:00',
    lunch: '12:00',
    dinner: '18:00'
  });
  const fetchMealTemplate = useStore((state) => state.fetchMealTemplate);

  useEffect(() => {
    fetchMealTemplate();
  }, [])

  useEffect(() => {
    const timing = {
      breakfast: '07:00',
      lunch: '12:00',
      dinner: '18:00',
      ...mealTime
    }
    setMealTimes(timing);
  }, [mealTime])

  const handleLocationChange = (country: string) => {
    const locationMap = {
      'US': { country: 'US', currency: 'USD', currencySymbol: '$' },
      'GB': { country: 'GB', currency: 'GBP', currencySymbol: '£' },
      'EU': { country: 'EU', currency: 'EUR', currencySymbol: '€' },
      'JP': { country: 'JP', currency: 'JPY', currencySymbol: '¥' },
      'AU': { country: 'AU', currency: 'AUD', currencySymbol: 'A$' },
      'NG': { country: 'NG', currency: 'NGN', currencySymbol: '₦' },
      'GH': { country: 'GH', currency: 'GHS', currencySymbol: 'GH₵' },
      'KE': { country: 'KE', currency: 'KES', currencySymbol: 'KSh' },
      'ZA': { country: 'ZA', currency: 'ZAR', currencySymbol: 'R' },
      'EG': { country: 'EG', currency: 'EGP', currencySymbol: 'E£' }
    };

    updateLocation(locationMap[country as keyof typeof locationMap]);
  };

  const fileInputRef = useRef<HTMLInputElement>(null);
  const [imageSrc, setImageSrc] = useState<string | null>(null);

  const handleButtonClick = () => {
    fileInputRef.current?.click();
  };

  const handleFileChange = async (event: React.ChangeEvent<HTMLInputElement>) => {
    const file = event.target.files?.[0];
    if (!file || !file.type.startsWith('image/')) {
      console.warn('Please select a valid image file.');
      return;
    }

    try {
      const localFormData = new FormData();
      const cloudName = "drive-cloud";
      localFormData.append("file", file);
      localFormData.append("upload_preset", "ezyvibes_user");

      const response = await fetch(
        `https://api.cloudinary.com/v1_1/${cloudName}/image/upload`,
        {
          method: "POST",
          body: localFormData,
        }
      );

      if (!response.ok) {
        throw new Error("Upload failed");
      }

      const data = await response.json();
      const uploadedImageUrl = data.secure_url;
      setImageSrc(uploadedImageUrl);
      formData.avatar = uploadedImageUrl;
      handleSave();
    } catch (error) {
      console.error("Error uploading image:", error);
    }
  };

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    if (name == "phone") {
      const phoneNumber = parsePhoneNumberFromString(value);
      const country = phoneNumber?.country // e.g., "NG", "US"
    }
    setFormData({
      ...formData,
      [name]: value
    });

  };

  const handleToggleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const { name, checked } = e.target;
    setFormData({
      ...formData,
      notifications: {
        ...formData.notifications,
        [name]: checked
      }
    });
  };

  const handleRadioChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    setFormData({
      ...formData,
      measurementSystem: e.target.value as 'metric' | 'imperial'
    });
  };

  const handleMealTimeChange = (type: 'breakfast' | 'lunch' | 'dinner', time: string) => {
    setIsLoading(true);
    const updatedMealTimes = { ...mealTime, [type]: time }
    setMealTimes(updatedMealTimes)
    updateMealTime(updatedMealTimes);
    setIsLoading(false);

    // setFormData(prev => ({
    //   ...prev,
    //   userPreferences: {
    //     ...prev.userPreferences,
    //     mealTimes: prev.userPreferences.mealTimes.map(mt =>
    //       mt.type === type ? { ...mt, time } : mt
    //     )
    //   }
    // }));
  };

  const handleSave = async () => {
    if (!user?.id) {
      addNotification({
        title: 'Error',
        message: 'You must be logged in to save profile changes',
        type: 'error'
      });
      return;
    }

    try {
      setIsLoading(true);

      // Update profile
      await updateUserProfile({
        id: user.id,
        firstName: formData.name.split(' ')[0],
        lastName: formData.name.split(' ')[1],
        email: formData.email,
        phone: formData.phone,
        bio: formData.bio,
        location: formData.location,
        timeZone: formData.timezone,
        avatar: formData.avatar
      });

      // Update preferences
      await updatePreferences({
        measurementSystem: formData.measurementSystem,
        notifications: formData.notifications,
        mealTimes: formData.userPreferences.mealTimes
      });

      setProfile(formData);
      setIsEditing(false);

      addNotification({
        title: 'Success',
        message: 'Profile updated successfully',
        type: 'success'
      });
    } catch (error) {
      console.error('Error saving profile:', error);
      addNotification({
        title: 'Error',
        message: 'Failed to update profile. Please try again.',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  const handleCancel = () => {
    setFormData(profile);
    setIsEditing(false);
  };

  const handlePasswordUpdate = async () => {
    if (!user?.id) {
      addNotification({
        title: 'Error',
        message: 'You must be logged in to change your password',
        type: 'error'
      });
      return;
    }

    setPasswordError('');
    setIsLoading(true);

    if (newPassword !== confirmPassword) {
      setPasswordError('New passwords do not match');
      setIsLoading(false);
      return;
    }

    if (newPassword.length < 6) {
      setPasswordError('Password must be at least 6 characters');
      setIsLoading(false);
      return;
    }

    try {
      await updateUserPassword(currentPassword, newPassword);
      // Clear password fields
      setCurrentPassword('');
      setNewPassword('');
      setConfirmPassword('');

      addNotification({
        title: 'Success',
        message: 'Password updated successfully',
        type: 'success'
      });
    } catch (error: any) {
      setPasswordError(error.message);
      addNotification({
        title: 'Error',
        message: error.message || 'Failed to update password',
        type: 'error'
      });
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <div className="space-y-6">
      <h1 className="text-2xl font-bold text-gray-800 dark:text-white">My Profile</h1>

      <div className="bg-white dark:bg-gray-800 rounded-xl overflow-hidden shadow-sm border border-gray-200 dark:border-gray-700">
        <div className="bg-gradient-to-r from-secondary to-secondary/80 h-16 relative">
          <div className="absolute -bottom-16 left-6">
            <div className="relative">
              <input
                type="file"
                accept="image/*"
                ref={fileInputRef}
                style={{ display: 'none' }}
                onChange={handleFileChange}
              />
              <img
                src={imageSrc ? imageSrc : profile.avatar}
                alt={profile.name}
                className="w-32 h-32 rounded-full border-4 border-white dark:border-gray-800 object-cover"
              />
              <button onClick={handleButtonClick} className="absolute bottom-0 right-0 bg-white dark:bg-gray-800 p-2 rounded-full shadow-md hover:bg-gray-50 dark:hover:bg-gray-700 transition-colors">
                <Camera size={16} className="text-gray-600 dark:text-gray-400" />
              </button>
            </div>
          </div>
        </div>

        <div className="mt-16 px-6 pb-6">
          <div className="flex flex-col sm:flex-row sm:items-center justify-between">
            <div>
              <h2 className="text-2xl font-bold text-gray-800 dark:text-white">{profile.name}</h2>
              {profile.location && <p className="text-gray-600 dark:text-gray-400 flex items-center mt-1">
                <Globe size={16} className="mr-1" />
                {profile.location}
              </p>}
            </div>
          </div>

          {/* Profile Tabs */}
          <div className="mt-8 border-b border-gray-200 dark:border-gray-700">
            <nav className="flex space-x-8">
              <button
                className={`pb-4 px-1 font-medium text-sm ${activeTab === 'info'
                  ? 'text-secondary border-b-2 border-secondary'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                onClick={() => setActiveTab('info')}
              >
                Personal Info
              </button>
              <button
                className={`pb-4 px-1 font-medium text-sm ${activeTab === 'preferences'
                  ? 'text-secondary border-b-2 border-secondary'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                onClick={() => setActiveTab('preferences')}
              >
                Preferences
              </button>
              <button
                className={`pb-4 px-1 font-medium text-sm ${activeTab === 'security'
                  ? 'text-secondary border-b-2 border-secondary'
                  : 'text-gray-500 dark:text-gray-400 hover:text-gray-700 dark:hover:text-gray-300'
                  }`}
                onClick={() => setActiveTab('security')}
              >
                Security
              </button>
            </nav>
          </div>

          <div className="mt-6">
            {/* Personal Info Tab */}
            {activeTab === 'info' && (
              <div className="space-y-6">
                {!isEditing && (
                  <button
                    onClick={() => setIsEditing(true)}
                    className="mt-4 mb-4 sm:mt-0 px-3 py-2 text-sm bg-[#5CE1E695] text-white rounded-lg hover:bg-secondary/90 transition-colors"
                  >
                    Edit Profile
                  </button>
                )}
                {isEditing ? (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <label htmlFor="name" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Full Name
                        </label>
                        <input
                          type="text"
                          id="name"
                          name="name"
                          value={formData.name}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>

                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Email Address
                        </label>
                        <input
                          type="email"
                          id="email"
                          name="email"
                          value={formData.email}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>

                      <div>
                        <label htmlFor="location" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Location
                        </label>
                        <input
                          type="text"
                          id="location"
                          name="location"
                          value={formData.location}
                          onChange={handleInputChange}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>

                      <div>
                        <label htmlFor="phone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Phone
                        </label>
                        <CustomPhoneInput
                          value={formData.phone ?? ""}
                          onChange={(value) => setFormData({ ...formData, phone: value })}
                        />
                        {/* <PhoneInput
                          id="phone"
                          name="phone"
                          international
                          defaultCountry={country || "US"}
                          value={formData.phone}
                          onChange={(value) => setFormData({ ...formData, phone: value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        /> */}
                      </div>

                      {/* <div>
                        <label htmlFor="timezone" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          Timezone
                        </label>
                        <select
                          id="timezone"
                          name="timezone"
                          value={formData.timezone}
                          onChange={(e) => setFormData({ ...formData, timezone: e.target.value })}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        >
                          <option value="Pacific Time (PT)">Pacific Time (PT)</option>
                          <option value="Mountain Time (MT)">Mountain Time (MT)</option>
                          <option value="Central Time (CT)">Central Time (CT)</option>
                          <option value="Eastern Time (ET)">Eastern Time (ET)</option>
                        </select>
                      </div> */}
                    </div>

                    {/* <div>
                      <label htmlFor="bio" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Bio
                      </label>
                      <textarea
                        id="bio"
                        name="bio"
                        rows={4}
                        value={formData.bio}
                        onChange={handleInputChange}
                        className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                      />
                    </div> */}

                    <div className="flex justify-end space-x-3">
                      <button
                        onClick={handleCancel}
                        className="px-4 py-2 border border-gray-300 dark:border-gray-600 text-gray-700 dark:text-gray-300 rounded-lg hover:bg-gray-50 dark:hover:bg-gray-700"
                        disabled={isLoading}
                      >
                        Cancel
                      </button>
                      <button
                        onClick={handleSave}
                        className="px-4 py-2 bg-secondary text-white rounded-lg hover:bg-secondary/90 disabled:opacity-50"
                        disabled={isLoading}
                      >
                        {isLoading ? 'Saving...' : 'Save Changes'}
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                      <div>
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Full Name</h3>
                        <p className="mt-1 text-gray-800 dark:text-white">{profile.name}</p>
                      </div>

                      <div>
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Email Address</h3>
                        <p className="mt-1 text-gray-800 dark:text-white">{profile.email}</p>
                      </div>

                      <div>
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Location</h3>
                        <p className="mt-1 text-gray-800 dark:text-white">{profile.location}</p>
                      </div>

                      <div>
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Phone</h3>
                        <p className="mt-1 text-gray-800 dark:text-white">{profile.phone}</p>
                      </div>
                      {/* <div>
                        <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Timezone</h3>
                        <p className="mt-1 text-gray-800 dark:text-white">{profile.timezone}</p>
                      </div> */}
                    </div>

                    {/* <div>
                      <h3 className="text-sm font-medium text-gray-500 dark:text-gray-400">Bio</h3>
                      <p className="mt-1 text-gray-800 dark:text-white">{profile.bio}</p>
                    </div> */}
                  </>
                )}
              </div>
            )}

            {/* Preferences Tab */}
            {activeTab === 'preferences' && (
              <div className="space-y-6">
                {/* <div>
                  <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-3">Measurement System</h3>
                  <div className="flex items-center space-x-4">
                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="measurementSystem"
                        value="metric"
                        checked={formData.measurementSystem === 'metric'}
                        onChange={handleRadioChange}
                        className="h-4 w-4 rounded-full border-gray-300 text-secondary focus:ring-secondary dark:border-gray-600 accent-secondary"
                      />
                      <span className="ml-2 text-gray-700 dark:text-gray-300">Metric (g, ml, °C)</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="radio"
                        name="measurementSystem"
                        value="imperial"
                        checked={formData.measurementSystem === 'imperial'}
                        onChange={handleRadioChange}
                        className="h-4 w-4 rounded-full border-gray-300 text-secondary focus:ring-secondary dark:border-gray-600 accent-secondary"
                      />
                      <span className="ml-2 text-gray-700 dark:text-gray-300">Imperial (oz, cups, °F)</span>
                    </label>
                  </div>
                </div> */}

                {/* <div>
                  <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-3">Location Settings</h3>
                  <div className="space-y-2">
                    <label className="block text-sm font-medium text-gray-700 dark:text-gray-300">
                      Country
                    </label>
                    <select
                      value={location.country}
                      onChange={(e) => handleLocationChange(e.target.value)}
                      className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                    >
                      <option value="US">United States (USD)</option>
                      <option value="GB">United Kingdom (GBP)</option>
                      <option value="EU">European Union (EUR)</option>
                      <option value="JP">Japan (JPY)</option>
                      <option value="AU">Australia (AUD)</option>
                      <option value="NG">Nigeria (NGN)</option>
                      <option value="GH">Ghana (GHS)</option>
                      <option value="KE">Kenya (KES)</option>
                      <option value="ZA">South Africa (ZAR)</option>
                      <option value="EG">Egypt (EGP)</option>
                    </select>
                    <p className="text-sm text-gray-500 dark:text-gray-400">
                      This setting affects recipe cost estimates and currency display
                    </p>
                  </div>
                </div> */}

                {/* <div>
                  <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-3">Notification Preferences</h3>

                  <div className="space-y-3">
                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="email"
                        checked={formData.notifications.email}
                        onChange={handleToggleChange}
                        className="h-4 w-4 rounded border-gray-300 text-secondary focus:ring-secondary dark:border-gray-600 accent-secondary"
                      />
                      <span className="ml-2 text-gray-700 dark:text-gray-300">Receive email notifications</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="newRecipes"
                        checked={formData.notifications.newRecipes}
                        onChange={handleToggleChange}
                        className="h-4 w-4 rounded border-gray-300 text-secondary focus:ring-secondary dark:border-gray-600 accent-secondary"
                      />
                      <span className="ml-2 text-gray-700 dark:text-gray-300">New recipe suggestions</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="weeklyDigest"
                        checked={formData.notifications.weeklyDigest}
                        onChange={handleToggleChange}
                        className="h-4 w-4 rounded border-gray-300 text-secondary focus:ring-secondary dark:border-gray-600 accent-secondary"
                      />
                      <span className="ml-2 text-gray-700 dark:text-gray-300">Weekly meal planning digest</span>
                    </label>

                    <label className="flex items-center">
                      <input
                        type="checkbox"
                        name="specialOffers"
                        checked={formData.notifications.specialOffers}
                        onChange={handleToggleChange}
                        className="h-4 w-4 rounded border-gray-300 text-secondary focus:ring-secondary dark:border-gray-600 accent-secondary"
                      />
                      <span className="ml-2 text-gray-700 dark:text-gray-300">Special offers and promotions</span>
                    </label>
                  </div>
                </div> */}

                <div>
                  <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-3">Preferred Meal Times</h3>
                  <div className="space-y-4">
                    {['breakfast', 'lunch', 'dinner'].map((mealType) => (
                      <div key={mealType} className="flex items-center">
                        <span className="w-24 text-gray-700 dark:text-gray-300 capitalize">{mealType}</span>
                        <input
                          type="time"
                          value={mealTimes[mealType] ? mealTimes[mealType].split(":")[0].padStart(2, "0") + ":" + mealTimes[mealType].split(":")[1] : "00:00"}
                          onChange={e =>
                            handleMealTimeChange(
                              mealType as 'breakfast' | 'lunch' | 'dinner',
                              e.target.value
                            )
                          }
                          min="00:00"
                          max="23:59"
                          step="60"  // optional: forces minute precision
                          pattern="^([01]\d|2[0-3]):[0-5]\d$"  // extra regex safety for browsers that ignore type="time"
                          className="ml-4 px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg
             focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary accent-secondary-500
             bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                        />
                      </div>
                    ))}
                  </div>
                </div>

                <div className="pt-4 flex justify-end">
                  <button
                    onClick={handleSave}
                    className="px-4 py-2 bg-[#5CE1E695] text-white rounded-lg hover:bg-secondary/90 disabled:opacity-50"
                    disabled={isLoading}
                  >
                    {isLoading ? 'Saving...' : 'Save Preferences'}
                  </button>
                </div>
              </div>
            )}

            {/* Security Tab */}
            {activeTab === 'security' && (
              <div className="space-y-6">
                <div>
                  <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-3">Change Password</h3>

                  <div className="space-y-4">
                    {passwordError && (
                      <div className="bg-red-50 dark:bg-red-900/30 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-300 px-4 py-3 rounded-lg text-sm">
                        {passwordError}
                      </div>
                    )}

                    <div>
                      <label htmlFor="currentPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Current Password
                      </label>
                      <div className="relative">
                        <input
                          type={showCurrentPassword ? "text" : "password"}
                          id="currentPassword"
                          value={currentPassword}
                          onChange={(e) => setCurrentPassword(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary bg-white dark:bg-gray-700 text-gray-900 dark:text-white pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowCurrentPassword(!showCurrentPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                          {showCurrentPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="newPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        New Password
                      </label>
                      <div className="relative">
                        <input
                          type={showNewPassword ? "text" : "password"}
                          id="newPassword"
                          value={newPassword}
                          onChange={(e) => setNewPassword(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary bg-white dark:bg-gray-700 text-gray-900 dark:text-white pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowNewPassword(!showNewPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                          {showNewPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                      </div>
                    </div>

                    <div>
                      <label htmlFor="confirmPassword" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                        Confirm New Password
                      </label>
                      <div className="relative">
                        <input
                          type={showConfirmPassword ? "text" : "password"}
                          id="confirmPassword"
                          value={confirmPassword}
                          onChange={(e) => setConfirmPassword(e.target.value)}
                          className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:outline-none focus:ring-2 focus:ring-secondary focus:border-secondary bg-white dark:bg-gray-700 text-gray-900 dark:text-white pr-10"
                        />
                        <button
                          type="button"
                          onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                          className="absolute inset-y-0 right-0 pr-3 flex items-center text-gray-400 hover:text-gray-600 dark:hover:text-gray-300"
                        >
                          {showConfirmPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                        </button>
                      </div>
                    </div>
                  </div>

                  <div className="pt-4 flex justify-end">
                    <button
                      onClick={handlePasswordUpdate}
                      className="px-4 py-2 bg-[#5CE1E695] text-white rounded-lg hover:bg-secondary/90 disabled:opacity-50"
                      disabled={isLoading}
                    >
                      {isLoading ? 'Updating...' : 'Update Password'}
                    </button>
                  </div>
                </div>

                <div className="border-t border-gray-200 dark:border-gray-700 pt-6">
                  <h3 className="text-lg font-medium text-gray-800 dark:text-white mb-3">Delete Account</h3>
                  <p className="text-gray-600 dark:text-gray-400 mb-4">
                    Once you delete your account, there is no going back. Please be certain.
                  </p>
                  <button className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700">
                    Delete Account
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
    </div >
  );
};

export default Profile;